import React, {
  createContext, useContext, useEffect, useRef, useState, useMemo
} from 'react';
import { node, string, bool } from 'prop-types';

import { useSpecialBuyApi } from '../api/useSpecialBuyApi';
import { noop } from '../utils/utils';

const getLogoSrc = (specialBuyType) => {
  const logoSrc = (specialBuyType === 'sbotd')
    ? 'sbotd-logo.png'
    : 'sbotw-badge.svg';

  return logoSrc;
};

const getSpecialBuyEndpoint = (specialBuyType) => {
  const specialBuyEndpoints = {
    sbotd: '/SpecialBuy/SpecialBuyOfTheDay',
    decor: '/SpecialBuy/DecorSpecialBuyOfTheWeek',
    pro: '/SpecialBuy/ProSpecialBuyOfTheWeek',
  };

  return specialBuyEndpoints[specialBuyType];
};

const getIconSrcForCategory = (category) => category.products[0].thumbnail;

const getFallbackValues = (specialBuyType) => ({
  specialBuyType,
  logoSrc: getLogoSrc(specialBuyType),
  tagline: (specialBuyType === 'sbotd')
    ? 'Today\'s Big Savings All in One Place'
    : 'This Week\'s Big Savings All in One Place',
  endDate: null,
  categories: [],
  selectedCategory: null,
  specialBuyEndpoint: getSpecialBuyEndpoint(specialBuyType),
  isFallbackBanner: true,
});

const getSanitaryProducts = (products) => (
  products?.filter((product) => (
    product.inventoryAvailable && product.image && product.currentPrice
  ))
  || []
);

const getFulfillableCategories = (categories) => (
  categories?.map((category) => ({
    ...category,
    products: getSanitaryProducts(category.products)
  })).filter((category) => category.products.length)
  || []
);

const isSanitaryCategory = (category) => Boolean(
  category?.name
  && category.categoryId
  && category.products?.length
);

const isUnexpired = (endDate) => {
  // TODO: the following should work once endDate is an epoch timestamp
  // return Date.now() < (new Date(endDate)).getTime();
  // TODO: the following line is placeholder until epoch timestamp is implemented
  return Boolean(endDate);
};
export const defaultContextValue = {
  specialBuyType: '',
  logoSrc: '',
  tagline: '',
  startDate: null,
  endDate: null,
  categories: [],
  specialBuyEndpoint: '',
  isFallbackBanner: true,
  isSpecialBuyDataLoading: false,
  isCondensedLayout: false,
  selectedCategory: null,
  setSelectedCategory: noop,
  simpleBanner: false
};
const SpecialBuyContext = createContext(defaultContextValue);
const useSpecialBuy = () => useContext(SpecialBuyContext);

const SpecialBuyProvider = ({ children, specialBuyType, isCondensedLayout, simpleBanner }) => {
  const isMounted = useRef(true);
  const { data, loading /* , error, */ } = useSpecialBuyApi(specialBuyType);
  const [specialBuyData, setSpecialBuyData] = useState(getFallbackValues(specialBuyType));
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const displayableCategories = getFulfillableCategories(data?.categories)
        .filter(isSanitaryCategory)
        .map((category) => ({
          ...category,
          iconSrc: getIconSrcForCategory(category)
        }));

      const isUsableSpecialBuyData = Boolean(
        data?.tagline
        && data.startDate
        && isUnexpired(data.endDate)
        && displayableCategories.length
      );

      const newSpecialBuyData = isUsableSpecialBuyData
        ? {
          specialBuyType,
          tagline: data.tagline,
          startDate: data.startDate,
          endDate: data.endDate,
          categories: displayableCategories,
          logoSrc: getLogoSrc(specialBuyType),
          specialBuyEndpoint: getSpecialBuyEndpoint(specialBuyType),
          isFallbackBanner: false,
        }
        : getFallbackValues(specialBuyType);

      setSpecialBuyData(newSpecialBuyData);
      setSelectedCategory(displayableCategories[0] || null);
    }
  }, [specialBuyType, data, setSpecialBuyData, setSelectedCategory]);

  const contextValue = useMemo(() => ({
    ...specialBuyData,
    isSpecialBuyDataLoading: loading,
    isCondensedLayout,
    simpleBanner,
    selectedCategory,
    setSelectedCategory,
  }), [specialBuyData, loading, isCondensedLayout, simpleBanner, selectedCategory]);

  return (
    <SpecialBuyContext.Provider value={contextValue}>
      {children}
    </SpecialBuyContext.Provider>
  );
};

SpecialBuyProvider.propTypes = {
  children: node.isRequired,
  specialBuyType: string.isRequired,
  isCondensedLayout: bool.isRequired,
  simpleBanner: bool
};

SpecialBuyProvider.defaultProps = {
  simpleBanner: false
};

SpecialBuyProvider.displayName = 'SpecialBuyProvider';

export { SpecialBuyProvider, SpecialBuyContext, useSpecialBuy };
